import React, { useMemo, useState } from 'react';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import useEscapeKey from '../hooks/useEscapeKey';

const nameOf = (branches, id) => branches.find((b) => b.branch_id === id)?.name || id;

const initialBranches = (user, role) => {
  if (role === 'co_owner') return user.role === 'co_owner' ? (user.branch_ids || []) : [];
  if (user.role === 'co_owner') return user.pre_co_owner?.role === role ? (user.pre_co_owner.branch_ids || []) : [];
  return user.branch_ids || [];
};

// Change a staff member's role, incl. promote to / demote from franchise co-owner, with a confirm step.
export default function ChangeRoleDialog({ user, branches, roles, onSubmit, onClose }) {
  useEscapeKey(onClose);
  const startRole = user.role === 'co_owner' ? (user.pre_co_owner?.role || '') : 'co_owner';
  const [role, setRole] = useState(startRole);
  const [picked, setPicked] = useState(() => initialBranches(user, startRole));
  const [step, setStep] = useState('form');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const label = (r) => roles.find((x) => x.v === r)?.l || r;
  const isCo = role === 'co_owner';
  const list = useMemo(() => (isCo ? branches.filter((b) => b.is_franchise) : branches), [isCo, branches]);

  const pickRole = (r) => { setRole(r); setPicked(initialBranches(user, r)); setErr(''); };
  const toggle = (id) => setPicked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const next = () => {
    if (!role) { setErr('Pick the new role.'); return; }
    if (role === user.role && !isCo) { setErr('Pick a different role.'); return; }
    if (isCo && picked.length === 0) { setErr('Select at least one franchise branch.'); return; }
    setErr(''); setStep('confirm');
  };
  const apply = async () => {
    setBusy(true); setErr('');
    try { await onSubmit(role, picked); } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (d?.message || 'Role change failed'));
      setStep('form');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-3" data-testid="change-role-dialog">
        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2"><ShieldCheck size={16} className="text-orange-600" /> Change role · {user.name || user.email}</h3>
        {step === 'form' ? (
          <>
            <label className="block text-sm">New role
              <select value={role} onChange={(e) => pickRole(e.target.value)} data-testid="change-role-select"
                className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded text-sm">
                <option value="">— Pick a role —</option>
                {roles.filter((r) => r.v !== user.role || r.v === 'co_owner').map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </label>
            {user.role === 'co_owner' && !user.pre_co_owner?.role && (
              <p className="text-[11px] text-amber-700" data-testid="change-role-no-previous">Created as a co-owner, so there is no earlier role. Pick one.</p>
            )}
            {role && (
              <div>
                <div className="text-sm mb-1">{isCo ? 'Franchise branches (at least one)' : 'Branch access (empty = clinic-wide roles only)'}</div>
                {list.length === 0 ? (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="change-role-no-branches">
                    {isCo ? 'No franchise branches yet. Mark a branch as "Franchise branch (co-owner)" first.' : 'No branches.'}
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-44 overflow-y-auto">
                    {list.map((b) => (
                      <label key={b.branch_id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={picked.includes(b.branch_id)} onChange={() => toggle(b.branch_id)} data-testid={`change-role-branch-${b.branch_id}`} />
                        {b.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="change-role-confirm">
            <div><span className="text-slate-500">User:</span> <b>{user.name || user.email}</b></div>
            <div className="flex items-center gap-2"><span className="text-slate-500">Role:</span> <b data-testid="change-role-from">{label(user.role)}</b> <ArrowRight size={13} /> <b data-testid="change-role-to">{label(role)}</b></div>
            <div><span className="text-slate-500">Branches:</span> <span data-testid="change-role-branches">{picked.length ? picked.map((id) => nameOf(branches, id)).join(', ') : 'None (clinic-wide or untagged only)'}</span></div>
            <p className="text-[11px] text-slate-500">Takes effect on their very next request.</p>
          </div>
        )}
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="change-role-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={step === 'confirm' ? () => setStep('form') : onClose} data-testid="change-role-cancel"
            className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded">{step === 'confirm' ? 'Back' : 'Cancel'}</button>
          {step === 'form' ? (
            <button type="button" onClick={next} data-testid="change-role-next"
              className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded">Review</button>
          ) : (
            <button type="button" onClick={apply} disabled={busy} data-testid="change-role-apply"
              className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded disabled:opacity-50">{busy ? 'Applying…' : 'Confirm change'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
